/**
 * Monitor panel — custom keyword watch lists.
 *
 * User adds keywords via text input. On each event:batch, new events
 * are checked against keywords (case-insensitive match in title + description).
 * Shows matching events with keyword highlighted and match count badges.
 */

import { eventBus } from '@/core/events';
import type { NormalizedEvent } from '@/sources/base-source';
import { relativeTime } from '@/utils/time';

const STORAGE_KEY = 'sentinel-monitor-keywords';
const MAX_MATCHES = 500;

interface KeywordMatch {
  keyword: string;
  event: NormalizedEvent;
}

export function createMonitorPanel(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'monitor-panel';
  container.style.cssText = 'display: flex; flex-direction: column; height: 100%;';

  const cleanupFns: (() => void)[] = [];
  let renderPending = false;

  // State
  let keywords: string[] = loadKeywords();
  const matches: KeywordMatch[] = [];
  const matchCounts = new Map<string, number>();

  // --- Input controls ---
  const inputArea = document.createElement('div');
  inputArea.style.cssText = 'padding: 10px 12px; border-bottom: 1px solid var(--border-primary); flex-shrink: 0;';

  const inputRow = document.createElement('div');
  inputRow.style.cssText = 'display: flex; gap: 6px;';

  const input = document.createElement('input');
  input.type = 'text';
  input.placeholder = 'Add keyword\u2026';
  input.style.cssText = `
    flex: 1; font-size: 12px; padding: 5px 8px; border-radius: 4px;
    background: var(--bg-secondary); color: var(--text-primary);
    border: 1px solid var(--border-primary); outline: none;
  `;

  const addBtn = document.createElement('button');
  addBtn.type = 'button';
  addBtn.style.cssText = `
    font-size: 11px; padding: 5px 10px; border-radius: 4px; cursor: pointer;
    background: var(--accent); color: var(--bg-primary); border: none;
  `;
  addBtn.textContent = 'Add';

  inputRow.appendChild(input);
  inputRow.appendChild(addBtn);
  inputArea.appendChild(inputRow);

  // Keywords display
  const keywordsArea = document.createElement('div');
  keywordsArea.style.cssText = 'display: flex; flex-wrap: wrap; gap: 4px; margin-top: 8px;';
  inputArea.appendChild(keywordsArea);

  // Clear all button
  const clearAllBtn = document.createElement('button');
  clearAllBtn.type = 'button';
  clearAllBtn.style.cssText = `
    font-size: 10px; padding: 3px 8px; border-radius: 3px; cursor: pointer;
    background: transparent; color: var(--text-muted); border: 1px solid var(--border-primary);
    margin-top: 6px; display: none;
  `;
  clearAllBtn.textContent = 'Clear All';
  inputArea.appendChild(clearAllBtn);

  container.appendChild(inputArea);

  // --- Results area ---
  const resultsArea = document.createElement('div');
  resultsArea.style.cssText = 'flex: 1; overflow-y: auto; padding: 8px 12px;';
  container.appendChild(resultsArea);

  function scheduleRender(): void {
    if (renderPending) return;
    renderPending = true;
    requestAnimationFrame(() => {
      renderPending = false;
      renderKeywords();
      renderMatches();
    });
  }

  // --- Helpers ---
  function loadKeywords(): string[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  function saveKeywords(): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(keywords));
  }

  function addKeyword(kw: string): void {
    const trimmed = kw.trim();
    if (!trimmed || keywords.includes(trimmed.toLowerCase())) return;
    keywords.push(trimmed.toLowerCase());
    saveKeywords();
    renderKeywords();
  }

  function removeKeyword(kw: string): void {
    keywords = keywords.filter(k => k !== kw);
    saveKeywords();
    // Remove matches for this keyword
    for (let i = matches.length - 1; i >= 0; i--) {
      if (matches[i].keyword === kw) matches.splice(i, 1);
    }
    matchCounts.delete(kw);
    renderKeywords();
    renderMatches();
  }

  function clearAll(): void {
    keywords = [];
    matches.length = 0;
    matchCounts.clear();
    saveKeywords();
    renderKeywords();
    renderMatches();
  }

  function renderKeywords(): void {
    keywordsArea.textContent = '';
    clearAllBtn.style.display = keywords.length > 0 ? 'inline-block' : 'none';

    for (const kw of keywords) {
      const chip = document.createElement('span');
      chip.style.cssText = `
        display: inline-flex; align-items: center; gap: 4px;
        font-size: 10px; padding: 2px 8px; border-radius: 10px;
        background: var(--bg-secondary); color: var(--text-primary);
        border: 1px solid var(--border-primary);
      `;

      const kwText = document.createElement('span');
      kwText.textContent = kw;
      chip.appendChild(kwText);

      const count = matchCounts.get(kw) ?? 0;
      if (count > 0) {
        const badge = document.createElement('span');
        badge.style.cssText = `
          font-size: 9px; font-weight: 600; padding: 0 4px; border-radius: 6px;
          background: var(--accent); color: var(--bg-primary);
        `;
        badge.textContent = String(count);
        chip.appendChild(badge);
      }

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.style.cssText = `
        font-size: 10px; background: none; border: none; cursor: pointer;
        color: var(--text-muted); padding: 0 2px; line-height: 1;
      `;
      removeBtn.textContent = '\u00D7';
      removeBtn.onclick = (e) => { e.stopPropagation(); removeKeyword(kw); };
      chip.appendChild(removeBtn);

      keywordsArea.appendChild(chip);
    }
  }

  function renderMatches(): void {
    resultsArea.textContent = '';

    if (matches.length === 0) {
      const msg = document.createElement('div');
      msg.style.cssText = 'padding: 24px 8px; text-align: center; color: var(--text-muted); font-size: 12px;';
      msg.textContent = keywords.length === 0
        ? 'Add keywords to start monitoring'
        : 'No matching events yet';
      resultsArea.appendChild(msg);
      return;
    }

    // Show most recent first, max 100
    const display = matches.slice(-100).reverse();

    for (const match of display) {
      const item = document.createElement('div');
      item.style.cssText = `
        padding: 6px 8px; margin-bottom: 4px; border-radius: 4px;
        background: var(--bg-secondary); border-left: 2px solid var(--accent);
      `;

      // Keyword tag
      const kwTag = document.createElement('span');
      kwTag.style.cssText = `
        font-size: 9px; padding: 1px 5px; border-radius: 6px;
        background: var(--accent); color: var(--bg-primary); font-weight: 600;
        margin-right: 6px;
      `;
      kwTag.textContent = match.keyword;
      item.appendChild(kwTag);

      // Title
      const title = document.createElement('span');
      title.style.cssText = 'font-size: 11px; color: var(--text-primary);';
      title.textContent = match.event.title;
      item.appendChild(title);

      // Meta
      const meta = document.createElement('div');
      meta.style.cssText = 'font-size: 10px; color: var(--text-muted); margin-top: 2px;';
      meta.textContent = `${match.event.source} \u00B7 ${relativeTime(match.event.timestamp)}`;
      item.appendChild(meta);

      resultsArea.appendChild(item);
    }
  }

  function checkEvents(events: NormalizedEvent[]): void {
    if (keywords.length === 0) return;

    let newMatches = false;
    for (const ev of events) {
      const text = `${ev.title} ${ev.description ?? ''}`.toLowerCase();
      for (const kw of keywords) {
        if (text.includes(kw)) {
          matches.push({ keyword: kw, event: ev });
          matchCounts.set(kw, (matchCounts.get(kw) ?? 0) + 1);
          newMatches = true;
        }
      }
    }

    // Cap matches array to prevent unbounded memory growth
    if (matches.length > MAX_MATCHES) {
      matches.splice(0, matches.length - MAX_MATCHES);
    }

    if (newMatches) {
      scheduleRender();
    }
  }

  // --- Event handlers ---
  addBtn.onclick = () => {
    addKeyword(input.value);
    input.value = '';
    input.focus();
  };

  input.onkeydown = (e) => {
    if (e.key === 'Enter') {
      addKeyword(input.value);
      input.value = '';
    }
  };

  clearAllBtn.onclick = clearAll;

  // Subscribe to event batches
  cleanupFns.push(
    eventBus.on('event:batch', (events) => checkEvents(events)),
  );

  // Initial render
  renderKeywords();
  renderMatches();

  (container as HTMLElement & { destroy?: () => void }).destroy = () => {
    for (const fn of cleanupFns) fn();
    cleanupFns.length = 0;
  };

  return container;
}
